import { useId } from 'react';
import { Button, TextButton } from '../ui';
import { OtpInput } from './OtpInput';
import { ResendCountdown } from './ResendCountdown';
import { useCodeVerification } from '../../../lib/use-code-verification';
import { wixLogout } from '../../../lib/logout';
import { PHONE_CONFLICT_CARE_URL } from '../../../lib/customer-care';
import { useTranslation } from '../../../lib/i18n';
import styles from './CodeScreen.module.css';

type CodeVerification = ReturnType<typeof useCodeVerification>;

// The code step's fields. Rendered only by the verification dialog now — the full-screen version is gone
// and `/onboarding/code` forwards to `/welcome` (see onboarding-gate's RETIRED_ONBOARDING). The hook is
// passed in rather than called again: the dialog's heading needs the number it resolves, and a second
// call would mount a second, independent copy of the step's state.
export function CodeStepBody({ code }: { code: CodeVerification }) {
  const { t } = useTranslation();
  const sending = code.resend === 'sending';
  // One wait, both channels. Alternating SMS → WhatsApp → SMS would otherwise walk straight through a
  // per-button cooldown and send three codes in as many seconds, which is the thing being prevented —
  // and the server throttles the CALLER, so the second channel would only earn a 429 anyway.
  const waiting = code.resendIn > 0;
  // The wait is a standing countdown under the buttons: a ring that drains beside the seconds left, the
  // same thing both phones draw. It used to be a tooltip opened by hover or focus, which left everyone who
  // did neither looking at two grey buttons with no stated reason, and a still line reads as a step that
  // is stuck. The ring moving is what says the wait has an end.
  //
  // The countdown stands down while the 429's own message is up, and only for THAT error: the message
  // counts the same seconds itself, so showing both states the wait twice. Every other error, a wrong
  // code most of all, says nothing about the wait, so the countdown is then the only thing explaining the
  // grey and must stay.
  //
  // `aria-disabled` is deliberately NOT gated on this. It tracks the cooldown and nothing else, because
  // the press is refused by `sendCode` against the deadline whatever is on screen: dropping it while a
  // wrong-code error showed left both buttons looking live and silently doing nothing.
  const countdownShown = waiting && code.errorKind !== 'tooSoon';
  // Per INSTANCE, not a constant: `CodeStepBody` is the phone modal's body AND the post-purchase
  // takeover's, and `AppFrame` can have both mounted. Two elements sharing one id makes
  // `aria-describedby` resolve to whichever came first in the DOM, so the takeover a user is looking at
  // would announce the dialog's wait underneath it.
  const countdownId = useId();

  const waitProps = {
    // The whole cooldown, every time. `aria-disabled` rather than `disabled` keeps the button in the tab
    // order, so a keyboard or screen-reader user who lands on it hears why it is grey.
    ...(waiting ? { 'aria-disabled': true as const } : {}),
    // Read once, on focus, rather than re-announced every second the way a live region would be.
    ...(countdownShown ? { 'aria-describedby': countdownId } : {}),
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        code.submit();
      }}
    >
      <OtpInput code={code} invalid={code.errorKind === 'code'} />

      {code.error && !code.phoneInUse && (
        <p className={styles.error} role="alert">
          {code.error}
        </p>
      )}

      {code.phoneInUse ? (
        <div className={styles.inUse} role="alert">
          <p className={styles.inUseTitle}>{t('onboarding.code.phoneInUse.title')}</p>
          <p className={styles.inUseBody}>{t('onboarding.code.phoneInUse.body')}</p>
          <div className={styles.inUseActions}>
            <Button
              variant="outlined"
              onClick={() => {
                code.logPageClickBi('phone_in_use_use_another');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.phoneInUse.useAnother')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                code.logPageClickBi('phone_in_use_sign_in_other');
                wixLogout();
              }}
            >
              {t('onboarding.code.phoneInUse.signInOther')}
            </Button>
            {/* The third way out, and the only one that helps the person whose number is already on the
                account they are trying to get INTO: for them "use another number" and "sign in with
                another email" are both wrong answers, and what is left is a human. Same hand-off the two
                phones offer on the same failure (`SupportHandoff` there). */}
            <Button
              variant="text"
              onClick={() => {
                code.logPageClickBi('phone_in_use_contact_support');
                window.open(PHONE_CONFLICT_CARE_URL, '_blank', 'noopener,noreferrer');
              }}
            >
              {t('onboarding.code.phoneInUse.contactSupport')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.verify}>
            <Button size="large" fullWidth disabled={!code.complete} loading={code.submitting} onClick={code.submit}>
              {t('onboarding.code.symphony.cta')}
            </Button>
          </div>

          {/* Both are disabled while either send is in flight — otherwise the idle one looks live and
              swallows the click. The WAIT is different: it uses `aria-disabled`, so the button keeps the
              DS's disabled look (`.root[aria-disabled='true']` is styled with `:disabled`) while staying
              focusable. The press itself is refused by `sendCode`, which reads the deadline. */}
          <div className={styles.resend}>
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method !== 'whatsapp'}
              onClick={() => void code.sendCode('sms', 'resend_sms')}
              {...waitProps}
            >
              {t('onboarding.code.symphony.resend.sms')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              disabled={sending}
              loading={sending && code.method === 'whatsapp'}
              onClick={() => void code.sendCode('whatsapp', 'resend_whatsapp')}
              {...waitProps}
            >
              {t('onboarding.code.symphony.resend.whatsapp')}
            </Button>
          </div>

          {/* One countdown for both buttons: they share one wait, so two would only repeat each other.

              There is deliberately no "a new code is on its way" line: the buttons going grey again, and
              the ring filling back up, IS that confirmation. */}
          {countdownShown ? (
            <ResendCountdown
              id={countdownId}
              className={styles.countdown}
              label={t('onboarding.code.symphony.resendIn', { seconds: code.resendIn })}
              left={code.resendLeft}
            />
          ) : null}

          <div className={styles.footer}>
            <TextButton
              size="small"
              underline
              onClick={() => {
                code.logPageClickBi('use_different_number');
                code.toPhoneStep();
              }}
            >
              {t('onboarding.code.useDifferentNumber')}
            </TextButton>
          </div>
        </>
      )}
    </form>
  );
}
